import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';

export default function Dashboard() {
  const [projects, setProjects] = useState([]);
  const [runs, setRuns] = useState([]);
  const [deployments, setDeployments] = useState([]);
  const [monitoring, setMonitoring] = useState({});

  useEffect(() => {
    api.get('/projects').then(r => setProjects(r.data)).catch(() => {});
    api.get('/pipelines/runs').then(r => setRuns(r.data)).catch(() => {});
    api.get('/deployments').then(r => setDeployments(r.data)).catch(() => {});
    api.get('/monitoring/status').then(r => setMonitoring(r.data)).catch(() => {});
  }, []);

  const statCards = [
    { label: 'Projects', value: projects.length, color: '#1a56db', bg: '#dbeafe', icon: '📁' },
    { label: 'Active Runs', value: runs.filter(r => r.status === 'RUNNING').length, color: '#d97706', bg: '#fef3c7', icon: '🔄' },
    { label: 'Deployments', value: deployments.length, color: '#059669', bg: '#d1fae5', icon: '🚀' },
    { label: 'Apps Up', value: Object.values(monitoring).filter(m => m.status === 'UP').length, color: '#059669', bg: '#d1fae5', icon: '✅' },
  ];

  const statusBadge = (status) => {
    const map = { SUCCESS: 'badge-success', FAILED: 'badge-danger', RUNNING: 'badge-warning', QUEUED: 'badge-info', PENDING: 'badge-gray' };
    return <span className={`badge ${map[status] || 'badge-gray'}`}>{status}</span>;
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: '#0f172a' }}>Dashboard</h1>
        <p style={{ color: '#64748b', fontSize: 14 }}>Overview of all CLTS applications and pipelines</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        {statCards.map(c => (
          <div key={c.label} className="card" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: c.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>{c.icon}</div>
            <div>
              <div style={{ fontSize: 24, fontWeight: 700, color: c.color }}>{c.value}</div>
              <div style={{ fontSize: 13, color: '#6b7280' }}>{c.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600 }}>Recent Pipeline Runs</h3>
            <Link to="/pipelines" style={{ fontSize: 13 }}>View all</Link>
          </div>
          {runs.length === 0 ? <p style={{ color: '#9ca3af', fontSize: 14 }}>No pipeline runs yet</p> : (
            <table>
              <thead><tr><th>Pipeline</th><th>Status</th><th>Time</th></tr></thead>
              <tbody>
                {runs.slice(0, 8).map(r => (
                  <tr key={r.id}>
                    <td style={{ fontWeight: 500 }}>#{r.id}</td>
                    <td>{statusBadge(r.status)}</td>
                    <td style={{ color: '#6b7280', fontSize: 13 }}>{r.createdAt ? new Date(r.createdAt).toLocaleString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600 }}>Application Status</h3>
            <Link to="/projects" style={{ fontSize: 13 }}>View all</Link>
          </div>
          {projects.length === 0 ? <p style={{ color: '#9ca3af', fontSize: 14 }}>No projects configured</p> : (
            <table>
              <thead><tr><th>Project</th><th>Platform</th><th>Health</th></tr></thead>
              <tbody>
                {projects.map(p => {
                  const health = monitoring[p.id];
                  return (
                    <tr key={p.id}>
                      <td style={{ fontWeight: 500 }}>{p.name}</td>
                      <td><span className="badge badge-info">{p.platform}</span></td>
                      <td>{health ? statusBadge(health.status === 'UP' ? 'SUCCESS' : 'FAILED') : <span className="badge badge-gray">N/A</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="card" style={{ gridColumn: 'span 2' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600 }}>Recent Deployments</h3>
            <Link to="/deployments" style={{ fontSize: 13 }}>View all</Link>
          </div>
          {deployments.length === 0 ? <p style={{ color: '#9ca3af', fontSize: 14 }}>No deployments yet</p> : (
            <table>
              <thead><tr><th>Project</th><th>Environment</th><th>Version</th><th>Strategy</th><th>Status</th><th>Deployed</th></tr></thead>
              <tbody>
                {deployments.slice(0, 5).map(d => (
                  <tr key={d.id}>
                    <td style={{ fontWeight: 500 }}>Project #{d.projectId}</td>
                    <td><span className="badge badge-info">{d.environment}</span></td>
                    <td>{d.version}</td>
                    <td>{d.strategy || '-'}</td>
                    <td>{statusBadge(d.status)}</td>
                    <td style={{ color: '#6b7280', fontSize: 13 }}>{d.deployedAt ? new Date(d.deployedAt).toLocaleString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
